import styled, {keyframes} from "styled-components";
import {hexToRGBA} from "@dropDesk/utils/helpers/colors";

type ContainerType = {
    background: string;
    border: string;
};

type TextType = {
    color: string;
};

type TrendType = {
    color: string;
    background: string;
};

type SkeletonBoneType = {
    background: string;
    highlight: string;
    width?: string;
    height?: string;
    borderRadius?: string;
};

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export const AttendantsCard = styled.div<ContainerType>`
  display: flex;
  flex-direction: column;
  background: ${(props) => props.background};
  border: 1px solid ${(props) => props.border};
  border-radius: 10px;
  overflow: hidden;
  height: 100%;
`;

export const AttendantsHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 16px 12px;
`;

export const AttendantsTitle = styled.span<TextType>`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.color};
`;

export const InfoButton = styled.div<{background: string; accent: string}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 24px;
  min-width: 24px;
  height: 24px;
  min-height: 24px;
  border-radius: 50%;
  cursor: pointer;

  &:hover {
    background: ${(props) => props.background};
    box-shadow: 0 0 0 2px ${(props) => hexToRGBA(props.accent, 0.58)};
    outline: none;
    transition: box-shadow .3s ease-out .1s, background-color .2s ease-out;
  }
`;

export const AttendantsTable = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const AttendantsHead = styled.div<{border: string}>`
  display: grid;
  grid-template-columns: minmax(0, 2fr) 120px 120px 140px;
  gap: 12px;
  padding: 8px 16px;
  border-top: 1px solid ${(props) => props.border};
  border-bottom: 1px solid ${(props) => props.border};
`;

export const HeadCell = styled.span<TextType>`
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => props.color};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

export const AttendantRow = styled.div<{border: string}>`
  display: grid;
  grid-template-columns: minmax(0, 2fr) 120px 120px 140px;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;

  &:not(:last-child) {
    border-bottom: 1px solid ${(props) => props.border};
  }
`;

export const AttendantInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const AttendantName = styled.span<TextType>`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RatingCell = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

export const RatingValue = styled.span<TextType>`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.color};
`;

export const TicketCount = styled.span<TextType>`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.color};
`;

export const TrendPill = styled.div<TrendType>`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 16px;
  background: ${(props) => props.background};
  color: ${(props) => props.color};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  width: fit-content;
`;

export const AttendantsNote = styled.div<TextType>`
  font-size: 12px;
  color: ${(props) => props.color};
  padding: 12px 16px 16px;
  line-height: 1.4;
  margin-top: auto;
`;

export const HighlightRow = styled.div<{border: string; background: string}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${(props) => props.border};
  background-color: ${(props) => props.background};
`;

export const HighlightText = styled.span<TextType>`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.color};
  line-height: 1.4;
`;

export const EmptyState = styled.div<TextType>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 32px 16px;
  font-size: 13px;
  color: ${(props) => props.color};
`;

export const SkeletonBone = styled.div<SkeletonBoneType>`
  width: ${(props) => props.width ?? '100%'};
  height: ${(props) => props.height ?? '14px'};
  border-radius: ${(props) => props.borderRadius ?? '6px'};
  background: linear-gradient(
    90deg,
    ${(props) => props.background} 25%,
    ${(props) => props.highlight} 50%,
    ${(props) => props.background} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;
